import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import Text from 'components/core/Text';
import PonderStepper from 'components/Ponder/PonderStepper';
import QuestionHeader from 'components/Ponder/QuestionHeader';
import { RatingCards } from 'components/Ponder/RatingQuestion';
import TagPicker, { toggleTag } from 'components/Ponder/TagPicker';
import { NotesInput, PrimaryAction } from 'components/Ponder/styles';
import { requestLogExternalWorkout } from 'actions/activities';
import { hideModal } from 'actions/modal';
import useSessionTags from 'hooks/useSessionTags';
import { StoreType } from 'reducers/types';
import { captureEvent } from 'utils/posthog';
import { Content, Background as Overlay } from '../styles';
import {
  CloseIcon,
  ModalContainer,
  FieldWrapper,
  FieldRow,
  TextInput,
  StepActions,
  StepBack,
} from './styles';
import {
  EASE_ORDER,
  EASE_RATING,
  PhotoField,
  WorkoutTypeInput,
  toCompletedAt,
  toDateInputValue,
  usePhotoPicker,
} from './shared';

type FormValues = {
  workoutType: string;
  completedAt: string;
  timeLength: string;
  tagIds: number[];
  rating: number;
  ponder: string;
};

const STEP = {
  activity: 'Activity',
  tags: 'Tags',
  feel: 'How it felt',
  notes: 'Notes',
};

/** Picking how it felt moves on; the short pause lets the selection register. */
const PICK_ADVANCE_DELAY_MS = 250;

const LogExternalWorkout: FC = () => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector((state: StoreType) => state.activities.isLoggingExternal);
  const submitError = useSelector((state: StoreType) => state.activities.logExternalError);

  const photo = usePhotoPicker();
  const sessionTags = useSessionTags();
  const [stepIndex, setStepIndex] = useState(0);
  const advanceTimer = useRef<number>();
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Skip the tags step only once we know there are none to pick from.
  const steps = [
    STEP.activity,
    ...(sessionTags === null || sessionTags.length > 0 ? [STEP.tags] : []),
    STEP.feel,
    STEP.notes,
  ];
  const currentIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[currentIndex];

  const goTo = useCallback((index: number) => {
    clearTimeout(advanceTimer.current);
    setStepIndex(index);
  }, []);

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(hideModal());
    },
    [dispatch],
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      captureEvent('external_workout_submitted', {
        duration_minutes: parseInt(values.timeLength, 10),
        rating: values.rating,
        tag_count: values.tagIds.length,
        has_photo: Boolean(photo.formData),
      });
      dispatch(
        requestLogExternalWorkout({
          workoutType: values.workoutType.trim(),
          timeLength: parseInt(values.timeLength, 10),
          tagIds: values.tagIds,
          rating: values.rating,
          completedAt: toCompletedAt(values.completedAt),
          ponder: values.ponder.trim() || undefined,
          file: photo.formData || undefined,
        }),
      );
    },
    [dispatch, photo.formData],
  );

  const today = toDateInputValue(new Date());

  return (
    <>
      <Overlay onClick={handleClose} />
      <Content>
        <CloseIcon onClick={handleClose} />
        <ModalContainer>
          <Formik
            initialValues={
              {
                workoutType: '',
                completedAt: today,
                timeLength: '',
                tagIds: [],
                rating: 0,
                ponder: '',
              } as FormValues
            }
            onSubmit={onSubmit}
          >
            {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
              const minutes = parseInt(values.timeLength, 10);
              const isActivityDone =
                !!values.workoutType.trim() && !!values.completedAt && minutes > 0;
              const canSubmit = isActivityDone && !!values.rating;

              const pickRating = (value: number) => {
                setFieldValue('rating', value);
                clearTimeout(advanceTimer.current);
                advanceTimer.current = window.setTimeout(
                  () => goTo(currentIndex + 1),
                  PICK_ADVANCE_DELAY_MS,
                );
              };

              const renderStep = () => {
                switch (step) {
                  case STEP.activity:
                    return (
                      <>
                        <QuestionHeader
                          title="What did you do?"
                          instruction="A run, a class, a climbing session, anything outside your GMB programs."
                        />
                        <FieldWrapper>
                          <WorkoutTypeInput
                            name="workoutType"
                            value={values.workoutType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onSelect={(label) => setFieldValue('workoutType', label)}
                            autoFocus
                          />
                        </FieldWrapper>
                        <FieldRow>
                          <div>
                            <Text variant="captionSmall" tag="p" transform="uppercase" mb={2}>
                              Date
                            </Text>
                            <TextInput
                              name="completedAt"
                              aria-label="Date"
                              type="date"
                              value={values.completedAt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              max={today}
                            />
                          </div>
                          <div>
                            <Text variant="captionSmall" tag="p" transform="uppercase" mb={2}>
                              Minutes
                            </Text>
                            <TextInput
                              name="timeLength"
                              aria-label="Minutes"
                              type="number"
                              inputMode="numeric"
                              value={values.timeLength}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g. 30"
                              min={1}
                              max={1440}
                            />
                          </div>
                        </FieldRow>
                      </>
                    );
                  case STEP.tags:
                    return (
                      <>
                        <QuestionHeader
                          title="Which words best describe it?"
                          instruction="Pick up to three tags. There's no wrong answers."
                        />
                        <TagPicker
                          tags={sessionTags ?? []}
                          selectedIds={values.tagIds}
                          onToggle={(tagId) =>
                            setFieldValue('tagIds', toggleTag(values.tagIds, tagId))
                          }
                        />
                      </>
                    );
                  case STEP.feel:
                    return (
                      <>
                        <QuestionHeader
                          title="How did it feel?"
                          instruction="Tap the one closest to how your body felt."
                        />
                        <RatingCards
                          rating={EASE_RATING}
                          displayOrder={EASE_ORDER}
                          value={values.rating || undefined}
                          onSelect={pickRating}
                        />
                      </>
                    );
                  default:
                    return (
                      <>
                        <QuestionHeader
                          title="Add your notes"
                          instruction="Anything worth remembering, and a photo if you like. Both optional."
                        />
                        <FieldWrapper>
                          <NotesInput
                            name="ponder"
                            aria-label="Notes"
                            minRows={5}
                            value={values.ponder}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Anything worth remembering."
                            maxLength={5000}
                          />
                        </FieldWrapper>
                        <PhotoField photo={photo} />
                        {submitError && (
                          <Text variant="textSmall" tag="p" color="ruby" mt={4}>
                            {submitError}
                          </Text>
                        )}
                      </>
                    );
                }
              };

              const isLastStep = step === STEP.notes;
              const canContinue =
                step === STEP.activity
                  ? isActivityDone
                  : step === STEP.feel
                    ? !!values.rating
                    : true;

              return (
                <>
                  <Text variant="sectionHeader" tag="h1" color="black" mb={4}>
                    LOG A WORKOUT
                  </Text>
                  <PonderStepper steps={steps} current={currentIndex} />
                  {renderStep()}
                  <StepActions>
                    {currentIndex > 0 && (
                      <StepBack onClick={() => goTo(currentIndex - 1)}>Back</StepBack>
                    )}
                    <PrimaryAction
                      isLoading={isLastStep && isSubmitting}
                      disabled={isLastStep ? !canSubmit : !canContinue}
                      handleClick={isLastStep ? handleSubmit : () => goTo(currentIndex + 1)}
                    >
                      {isLastStep ? 'Save' : 'Continue'}
                    </PrimaryAction>
                  </StepActions>
                </>
              );
            }}
          </Formik>
        </ModalContainer>
      </Content>
    </>
  );
};

export default LogExternalWorkout;
